import React, { useState } from 'react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Switch } from '@/components/ui/switch'

import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { Brain, Settings, Save } from 'lucide-react'
import { supabase } from '@/lib/supabase'
import { useToast } from '@/hooks/use-toast'

interface BotPatternProcessingSettingsProps {
  botId: string
  botName: string
  patternProcessingEnabled: boolean
  onSettingsUpdate: (enabled: boolean) => void
}

export const BotPatternProcessingSettings: React.FC<BotPatternProcessingSettingsProps> = ({
  botId,
  botName,
  patternProcessingEnabled,
  onSettingsUpdate
}) => {
  const [enabled, setEnabled] = useState(patternProcessingEnabled)
  const [isSaving, setIsSaving] = useState(false)
  const { toast } = useToast()

  const handleSave = async () => {
    setIsSaving(true)
    try {
      const { error } = await supabase
        .from('bots')
        .update({
          pattern_processing_enabled: enabled
        })
        .eq('id', botId)

      if (error) throw error

      onSettingsUpdate(enabled)
      toast({
        title: "Settings updated",
        description: `Pattern processing settings updated for ${botName}`,
      })
    } catch (error) {
      console.error('Error updating bot settings:', error)
      toast({
        title: "Error",
        description: "Failed to update bot settings",
        variant: "destructive"
      })
    } finally {
      setIsSaving(false)
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Brain className="h-5 w-5" />
          Pattern Processing Settings
        </CardTitle>
        <CardDescription>
          Configure AI pattern processing for {botName}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="flex items-center justify-between">
          <div className="space-y-0.5">
            <Label htmlFor="pattern-processing">Enable Pattern Processing</Label>
            <p className="text-sm text-muted-foreground">
              Use AI to make WIN/LOSS/SKIP decisions before signal routing
            </p>
          </div>
          <Switch
            id="pattern-processing"
            checked={enabled}
            onCheckedChange={setEnabled}
          />
        </div>

        {enabled && (
          <div className="bg-muted/30 rounded-lg p-4">
            <h4 className="text-sm font-medium mb-2">How it works:</h4>
            <ul className="text-sm text-muted-foreground space-y-1">
              <li>• <strong>WIN:</strong> Forward signal unchanged</li>
              <li>• <strong>LOSS:</strong> Reverse direction (LONG ↔ SHORT)</li>
              <li>• <strong>SKIP:</strong> Ignore signal completely</li>
            </ul>
            <p className="text-xs text-muted-foreground mt-2">
              Decisions are made based on current AI predictions from pattern analysis.
            </p>
          </div>
        )}

        <Button 
          onClick={handleSave} 
          disabled={isSaving}
          className="w-full"
        >
          <Save className="h-4 w-4 mr-2" />
          {isSaving ? 'Saving...' : 'Save Settings'}
        </Button>
      </CardContent>
    </Card>
  )
}